import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';

import { PatternlibService } from '../../global/runtime';
import { ClassNames, LhClose } from '../../utils/interfaces';

@Component({
  tag: 'patternlib-flyoutmenu',
  styleUrl: 'patternlib-flyoutmenu.scss',
  shadow: true,
})
export class PatternlibFlyoutmenu {
  @Element() hostElement: HTMLPatternlibFlyoutmenuElement;

  /**
   * Show the Flyoutmenu
   */
  @Prop() show?: boolean = false;

  /**
   * Set the width of the Flyoutmenu in px
   */
  @Prop() width = '280px';

  /**
   * Set the height of the Flyoutmenu
   */
  @Prop() height = 'auto';

  /**
   * Set the user name inside the Flyoutmenu
   */
  @Prop() userName?: string;

  /**
   * Set the user info inside the Flyoutmenu
   */
  @Prop() userInfo?: string;

  /**
   * Set the user initials inside the Flyoutmenu
   */
  @Prop() userInitials?: string;

  /**
   * Set the position of the Flyoutmenu
   */
  @Prop() flyoutPosition: 'top' | 'bottom' | 'auto' = 'auto';

  /**
   * Define the Flyoutmenu z-index as a number
   */
  @Prop() zIndex = 100;

  /**
   * Set the default alignment of the Flyoutmenu
   */
  @Prop() defaultAlign: 'left' | 'center' | 'right' | 'auto' = 'auto';

  /**
   * Wether the component is registered at the FlyoutGroups handler or not.
   */
  @Prop() useFlyoutGroups = true;

  /**
   * Flyout group name. Only works if useFlyoutGroups is true.
   */
  @Prop() groupName?: string;

  /**
   * Hide Arrow
   */
  @Prop() hideArrow = false;

  /**
   * Set offset (needed to set if the Flyoutmenu is used inisde of a scrollable container, to reposition based on the scrollTop value)
   */
  @Prop() offset = 0;

  /**
   * Define the gap betwwen the Flyoutmenu button and container
   */
  @Prop() gap = 16;

  @Watch('groupName')
  async onGroupNameChange() {
    if (this.useFlyoutGroups) {
      PatternlibService.unregisterFlyout(this, this.groupName);

      PatternlibService.registerFlyout(this, this.groupName);
    }
  }

  /**
   * Internal state if Flyoutmenu is open
   */
  @State() isOpen: boolean;

  /**
   * Emits when closeFlyoutmenu() was invoked.
   */
  @Event({ eventName: 'lhFlyoutClosed' }) flyoutClosed: EventEmitter<LhClose>;

  /**
   * Public function to open the Flyoutmenu
   */
  @Method()
  async openFlyoutmenu(): Promise<void> {
    this.isOpen = true;

    if (this.useFlyoutGroups) {
      PatternlibService.closeAllOtherFlyouts(this, this.groupName);
    }
  }

  /**
   * Public function to close the Flyoutmenu
   */
  @Method()
  async closeFlyoutmenu() {
    this.isOpen = false;
    this.flyoutClosed.emit();
  }

  @Watch('show')
  showWatcher(show: boolean) {
    this.isOpen = show;
    if (show) {
      window.addEventListener(
        'click',
        async (e: Event) => {
          const target = e.target as HTMLElement;
          if (!this.hostElement.contains(target)) {
            await this.closeFlyoutmenu();
          }
        },
        false,
      );
    }
    if (this.useFlyoutGroups && this.isOpen) {
      PatternlibService.closeAllOtherFlyouts(this, this.groupName);
    }
  }

  @Watch('isOpen')
  async isOpenWatcher(isOpen: boolean): Promise<void> {
    if (isOpen) {
      if (this.useFlyoutGroups) {
        PatternlibService.closeAllOtherFlyouts(this, this.groupName);
      }
    }
  }

  /**
   * Provide a method to monitor if the component gets clicked
   */
  @Listen('click', { target: 'window' })
  handleWindowClick(e: any) {
    if (
      e.target != this.hostElement &&
      this.isOpen &&
      this.show === false &&
      e.target.closest('[slot="flyoutmenu-button"]') === null &&
      e.target.closest('[slot="flyoutmenu-content"]') === null
    ) {
      this.isOpen = false;
    }

    const target = e.target as HTMLElement;
    if (!this.hostElement.contains(target)) {
      this.closeFlyoutmenu().catch(error => console.log(error.message));
    }
  }

  componentWillLoad() {
    this.isOpen = this.show;
  }

  async componentDidLoad() {
    if (this.useFlyoutGroups) {
      PatternlibService.registerFlyout(this, this.groupName);
    }
  }

  render() {
    const flyoutmenuContainerClass: ClassNames = {
      'flyoutmenu-container': true,
    };

    const flyoutmenuWrapperClasses: ClassNames = {
      'flyoutmenu-wrapper': true,
    };

    const flyoutmenuAvatarContainerClass: ClassNames = {
      'flyoutmenu-avatar-container': true,
    };

    const flyoutmenuUserNameClasses: ClassNames = {
      'flyoutmenu-user-name': true,
      'flex pt-[3px]': true,
    };

    const flyoutmenuUserInfoClasses: ClassNames = {
      'flyoutmenu-user-info': true,
      'flex pb-[3px]': true,
    };

    const flyoutmenuUserContainerClass: ClassNames = {
      'flyoutmenu-user-container': true,
    };

    const flyoutmenuUserWrapperClasses: ClassNames = {
      'flex flex-col self-center overflow-hidden min-h-[50px]': true,
    };

    return (
      <Host>
        <patternlib-overlay-container
          target="element"
          contentWidth={parseInt(this.width)}
          position={this.flyoutPosition}
          alignment={this.defaultAlign}
          show={this.isOpen}
          showArrow={!this.hideArrow}
        >
          <slot slot="element" name="flyoutmenu-button"></slot>
          <div slot="content">
            <div class={flyoutmenuContainerClass}>
              <div class={flyoutmenuWrapperClasses}>
                {this.userName != null && this.userInfo != null && this.userInitials != null && (
                  <div class={flyoutmenuUserContainerClass}>
                    <div class={flyoutmenuAvatarContainerClass}>
                      <patternlib-avatar initials={this.userInitials} no-states></patternlib-avatar>
                    </div>
                    <div class={flyoutmenuUserWrapperClasses}>
                      <div class={flyoutmenuUserNameClasses}>
                        <span title={this.userName}>{this.userName}</span>
                      </div>
                      <div class={flyoutmenuUserInfoClasses}>
                        <span title={this.userInfo}>{this.userInfo}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <slot name="flyoutmenu-content"></slot>
          </div>
        </patternlib-overlay-container>
      </Host>
    );
  }
}
